/**
 * AuthContext — manages Firebase user + role dari backend sync.
 *
 * Flow:
 * 1. onAuthChange fires saat Firebase Auth state berubah.
 * 2. Jika user login → ambil ID token → panggil /api/auth/sync untuk dapat role.
 * 3. Inject ID token ke ApiClient sebagai Bearer header.
 */

import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { api } from "@/lib/api";
import {
  onAuthChange,
  signInWithGoogle,
  signOutCurrent,
  getFreshIdToken,
} from "@/lib/firebase";
import type { AuthUser } from "@/types";

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  error: string | null;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Inject ID token provider ke ApiClient (sekali)
  useEffect(() => {
    api.setTokenProvider(getFreshIdToken);
  }, []);

  // Subscribe ke Firebase auth state
  useEffect(() => {
    const unsub = onAuthChange(async (fbUser) => {
      if (!fbUser) {
        setUser(null);
        setLoading(false);
        return;
      }

      try {
        const idToken = await fbUser.getIdToken();
        // Sync ke backend untuk dapat role (lazy-create user row + owner bootstrap)
        const synced = await api.syncUser();
        setUser({
          uid: synced.uid,
          email: synced.email,
          role: synced.role,
          idToken,
        });
      } catch (e) {
        console.error("Auth sync failed:", e);
        setError(e instanceof Error ? e.message : "Auth sync failed");
        setUser(null);
      } finally {
        setLoading(false);
      }
    });

    return () => unsub();
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      error,
      async signIn() {
        setError(null);
        // Tahan status loading sejak popup dibuka sampai onAuthChange selesai
        // sync role. Tanpa ini, redirect ke /dashboard sempat melihat
        // user=null & loading=false → ProtectedRoute berkedip "AUTH_REQUIRED".
        setLoading(true);
        try {
          await signInWithGoogle();
          // setLoading(false) dilakukan handler onAuthChange setelah sync.
        } catch (e) {
          setLoading(false);
          const msg = e instanceof Error ? e.message : "Sign in failed";
          setError(msg);
          throw e;
        }
      },
      async signOut() {
        await signOutCurrent();
        setUser(null);
      },
    }),
    [user, loading, error],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

/**
 * Hook untuk komponen yang butuh user (return null jika belum auth).
 * Gunakan di komponen: jika return null → render fallback / redirect.
 */
export function useMaybeUser(): AuthUser | null {
  const { user, loading } = useAuth();
  if (loading) return null;
  return user;
}